import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/logo/smartbusiness-white1.png";
import {
  Sparkles,
  ShieldCheck,
  Globe,
  Smartphone,
  Zap,
  Palette,
  Users,
  Search,
  Facebook,
  Instagram,
  Video,
  MessageCircle,
  PhoneCall,
} from "lucide-react";
import AccessibilityWidget from "../components/AccessibilityWidget";
import { pagesApi } from "../api/pagesApi";
import { usePublicLanguage } from "../hooks/usePublicLanguage";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

export default function Homepage() {
  const navigate = useNavigate();

  // ✅ language (cached + no flash)
  const { lang, setLang, t, isRTL, ready } = usePublicLanguage({
    apiBase: API_BASE,
    defaultLang: "ar",
  });

  const [pagesData, setPagesData] = useState([]);
  const [search, setSearch] = useState("");

  // Load pages
  useEffect(() => {
    const controller = new AbortController();

    async function loadPages() {
      try {
        const pages = await pagesApi.listPublic({ signal: controller.signal });
        setPagesData(pages || []);
      } catch (e) {
        if (e.name !== "AbortError") console.error("Failed to load pages:", e);
        setPagesData([]);
      }
    }

    loadPages();
    return () => controller.abort();
  }, []);

  // ✅ Defaults: if field missing, treat as true
  const readActive = (page) => (typeof page?.active === "boolean" ? page.active : true);
  const readVisible = (page) => (typeof page?.visible === "boolean" ? page.visible : true);

  // ✅ Public list = only active AND visible
  const publicPagesData = useMemo(() => {
    return (pagesData || []).filter((p) => readActive(p) && readVisible(p));
  }, [pagesData]);

  // Demo pages for users without any public pages
  const demoPages = useMemo(
    () => [
      {
        id: "demo-business",
        title: t("homepage.demo.business.title", "Demo Landing Page"),
        path: "/home",
        type: "demo",
        slug: "demo",
      },
    ],
    [t]
  );

  // Combine public pages with demo page
  const allPages = useMemo(() => {
    const userPages = (publicPagesData || []).map((page) => ({
      id: page.id,
      title: page.name || t("homepage.pages.untitled", "Untitled Page"),
      path: `/${page.slug}`,
      type: "user",
      slug: page.slug,
    }));

    return [...userPages, ...(userPages.length === 0 ? demoPages : [])];
  }, [publicPagesData, demoPages, t]);

  const filteredPages = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return allPages;
    return (allPages || []).filter((p) => {
      const hay = `${p.title} ${p.slug || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [allPages, search]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const SOCIAL = {
    facebook: "https://facebook.com",
    instagram: "https://instagram.com",
    tiktok: "https://tiktok.com",
    whatsapp: "https://wa.me/972524657778",
  };

  // Optional: first ever visit (no cache yet) show small loader
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white border rounded-2xl shadow-sm p-6">
          <div className="text-lg font-bold text-slate-900">Loading…</div>
          <div className="text-slate-600 mt-1">Preparing language</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen text-slate-900 bg-gradient-to-b from-slate-100 via-slate-50 to-slate-100 overflow-x-hidden relative"
      dir={isRTL ? "rtl" : "ltr"}
    >
      {/* subtle background */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(59,130,246,0.03),transparent_72%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,rgba(34,211,238,0.025),transparent_72%)]" />
        <div className="hidden sm:block absolute inset-0 opacity-[0.01] bg-[linear-gradient(to_right,rgba(15,23,42,0.16)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.16)_1px,transparent_1px)] bg-[size:110px_110px]" />
      </div>

      {/* NAVBAR */}
      <nav className="sticky top-0 z-50 border-b border-slate-800 bg-slate-950/90 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between gap-3 min-w-0">
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="flex items-center gap-3 min-w-0"
            >
              <div className="h-9 w-9 shrink-0 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-center shadow-sm">
                <img src={logo} alt="SmartBusiness" className="h-5 w-auto" />
              </div>
              <span className="text-base sm:text-lg font-semibold tracking-tight text-slate-100 truncate">
                {t("homepage.brand", "SmartBusiness")}
              </span>
            </button>

            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              {/* Language Switcher */}
              <div className="flex items-center gap-1 rounded-xl bg-slate-900/70 p-1 border border-slate-800 shrink-0">
                {["ar", "en", "he"].map((language) => (
                  <button
                    key={language}
                    onClick={() => setLang(language)}
                    className={`px-3 py-1.5 text-xs sm:text-sm rounded-lg transition-all ${
                      lang === language
                        ? "bg-slate-100 text-slate-950 shadow-sm"
                        : "text-slate-300 hover:text-slate-100 hover:bg-slate-800/70"
                    }`}
                  >
                    {language === "ar" ? "العربية" : language === "en" ? "EN" : "HE"}
                  </button>
                ))}
              </div>

              <button
                onClick={() => scrollToSection("showcase")}
                className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-800 bg-slate-900/60 text-slate-200 hover:bg-slate-800 transition font-medium"
              >
                {t("homepage.nav.examples", "Examples")}
              </button>

              <button
                onClick={() => scrollToSection("contact")}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 text-slate-950 font-semibold shadow-sm hover:bg-white transition focus:outline-none focus:ring-4 focus:ring-slate-400/40 shrink-0"
              >
                {t("homepage.nav.contact", "Contact")}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <header className="relative">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-10 sm:pt-20 sm:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className={`${isRTL ? "text-right" : "text-left"} text-center lg:text-start min-w-0`}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-50 border border-slate-200 text-sm text-slate-700 shadow-sm">
                <Sparkles className="h-4 w-4 text-cyan-700" />
                {t("homepage.hero.badge", "Modern landing pages for real businesses")}
              </div>

              <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-slate-900">
                {t("homepage.hero.title", "A professional landing page")}
                <span className="block text-blue-700">{t("homepage.hero.highlight", "built to convert")}</span>
              </h1>

              <p className="mt-5 text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto lg:mx-0">
                {t(
                  "homepage.hero.description",
                  "SmartBusiness is a modern system to create high-quality landing pages. Contact us to set up your account or to build the page for you — fast, clean, and professional."
                )}
              </p>

              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start sm:items-center">
                <a
                  href={SOCIAL.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex w-full sm:w-auto sm:min-w-[150px] justify-center items-center gap-2 px-5 py-3 rounded-xl bg-blue-600 text-white font-semibold shadow-sm hover:bg-blue-700 transition focus:outline-none focus:ring-4 focus:ring-blue-200/70"
                >
                  <MessageCircle className="h-5 w-5" />
                  {t("homepage.hero.whatsapp", "WhatsApp")}
                </a>

                <a
                  href="tel:0524657778"
                  className="inline-flex w-full sm:w-auto sm:min-w-[190px] justify-center items-center gap-2 px-5 py-3 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 transition font-semibold text-slate-900"
                >
                  <PhoneCall className="h-5 w-5" />
                  {t("homepage.hero.call", "Call 0524657778")}
                </a>
              </div>

              <div className="mt-8 flex flex-wrap gap-2 justify-center lg:justify-start">
                {[
                  { icon: <Globe className="h-4 w-4" />, label: t("homepage.hero.pill1", "RTL and LTR") },
                  { icon: <Smartphone className="h-4 w-4" />, label: t("homepage.hero.pill2", "Mobile-first") },
                  { icon: <Zap className="h-4 w-4" />, label: t("homepage.hero.pill3", "Fast performance") },
                  { icon: <ShieldCheck className="h-4 w-4" />, label: t("homepage.hero.pill4", "Clean design") },
                ].map((item, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-sm text-slate-700 shadow-sm"
                  >
                    {item.icon}
                    {item.label}
                  </span>
                ))}
              </div>
            </div>

            <div
              className={`relative w-full max-w-[520px] mx-auto overflow-hidden ${
                isRTL ? "lg:justify-self-start" : "lg:justify-self-end"
              }`}
            >
              <div className="rounded-3xl border border-slate-200 bg-slate-50 shadow-xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 bg-slate-100/60">
                  <span className="h-3 w-3 rounded-full bg-slate-300" />
                  <span className="h-3 w-3 rounded-full bg-slate-200" />
                  <span className="h-3 w-3 rounded-full bg-slate-100" />
                  <div className="ml-3 text-xs text-slate-500">
                    {t("homepage.hero.previewLabel", "Preview")}
                  </div>
                </div>

                <div className="p-6">
                  <div className="h-10 w-44 rounded-xl bg-slate-100 border border-slate-200 mb-6" />
                  <div className="space-y-3">
                    <div className="h-4 rounded bg-slate-100 w-full" />
                    <div className="h-4 rounded bg-slate-100 w-11/12" />
                    <div className="h-4 rounded bg-slate-100 w-10/12" />
                  </div>
                  <div className="mt-6 grid grid-cols-3 gap-3">
                    <div className="h-20 rounded-2xl bg-slate-100/60 border border-slate-200" />
                    <div className="h-20 rounded-2xl bg-slate-100/60 border border-slate-200" />
                    <div className="h-20 rounded-2xl bg-slate-100/60 border border-slate-200" />
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-6 -left-6 h-28 w-28 rounded-full bg-cyan-200/16 blur-3xl" />
              <div className="absolute -top-8 -right-8 h-36 w-36 rounded-full bg-blue-200/16 blur-3xl" />
            </div>
          </div>
        </div>
      </header>

      {/* Features */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
              {t("homepage.features.title", "A clean, modern experience")}
            </h2>
            <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
              {t(
                "homepage.features.subtitle",
                "Designed to look professional, load fast, and work perfectly in Arabic, English, and Hebrew."
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-4">
            {[
              {
                icon: <Globe className="h-6 w-6 text-cyan-700" />,
                title: t("homepage.features.multiLang.title", "RTL and LTR ready"),
                desc: t("homepage.features.multiLang.desc", "Perfect layout direction and typography."),
              },
              {
                icon: <Smartphone className="h-6 w-6 text-cyan-700" />,
                title: t("homepage.features.responsive.title", "Mobile-first"),
                desc: t("homepage.features.responsive.desc", "Looks great on phone, tablet, and desktop."),
              },
              {
                icon: <Zap className="h-6 w-6 text-cyan-700" />,
                title: t("homepage.features.fast.title", "Fast performance"),
                desc: t("homepage.features.fast.desc", "Optimized to feel smooth and quick."),
              },
              {
                icon: <Palette className="h-6 w-6 text-cyan-700" />,
                title: t("homepage.features.customizable.title", "Flexible design"),
                desc: t("homepage.features.customizable.desc", "Layouts that match your brand style."),
              },
              {
                icon: <Users className="h-6 w-6 text-cyan-700" />,
                title: t("homepage.features.trust.title", "Built to build trust"),
                desc: t("homepage.features.trust.desc", "Structured sections that present your offer clearly."),
              },
              {
                icon: <ShieldCheck className="h-6 w-6 text-cyan-700" />,
                title: t("homepage.features.control.title", "Managed setup"),
                desc: t("homepage.features.control.desc", "Contact us and we will set everything up for you."),
              },
            ].map((f, i) => (
              <div
                key={i}
                className="rounded-3xl border border-slate-200 bg-slate-50 p-6 hover:shadow-lg hover:shadow-slate-200/40 transition w-full max-w-[360px] justify-self-center min-w-0"
              >
                <div className="h-12 w-12 rounded-2xl bg-slate-100/60 border border-slate-200 flex items-center justify-center">
                  {f.icon}
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900 break-words">{f.title}</h3>
                <p className="mt-2 text-slate-600 break-words">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Showcase / Examples */}
      <section id="showcase" className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
            <div className={`${isRTL ? "text-right" : "text-left"} text-center lg:text-start min-w-0`}>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
                {t("homepage.showcase.title", "Examples")}
              </h2>
              <p className="mt-2 text-slate-600 max-w-2xl mx-auto lg:mx-0">
                {t("homepage.showcase.subtitle", "Open an example to see the design in action.")}
              </p>
            </div>

            <div className="w-full lg:w-[420px] min-w-0">
              <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 shadow-sm min-w-0">
                <Search className="h-5 w-5 text-slate-500 shrink-0" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("homepage.showcase.search", "Search pages...")}
                  className="w-full min-w-0 bg-transparent outline-none text-slate-900 placeholder:text-slate-400"
                />
              </div>
            </div>
          </div>

          {filteredPages.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-4">
              {filteredPages.map((page) => (
                <button
                  key={page.id}
                  onClick={() => page.path && navigate(page.path)}
                  className="group rounded-3xl border border-slate-200 bg-slate-50 hover:bg-slate-100/70 transition overflow-hidden shadow-sm hover:shadow-lg hover:shadow-slate-200/40 w-full max-w-[360px] justify-self-center text-left min-w-0"
                >
                  <div className="relative h-40 sm:h-44 bg-slate-900 overflow-hidden">
                    <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.35),transparent_60%)]" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <img
                        src={logo}
                        alt="SmartBusiness"
                        className="h-14 sm:h-16 w-auto max-w-[80%] drop-shadow-[0_10px_20px_rgba(0,0,0,0.35)]"
                      />
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="text-center text-lg sm:text-xl font-semibold text-slate-900 truncate">
                      {page.title}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-10 text-center shadow-sm">
              <p className="text-slate-600">{t("homepage.showcase.empty", "No results found.")}</p>
              <button
                onClick={() => setSearch("")}
                className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 transition font-semibold text-slate-900"
              >
                {t("homepage.showcase.clear", "Clear search")}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Contact CTA */}
      <section id="contact" className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 sm:pb-20">
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-8 sm:p-10 shadow-lg shadow-slate-200/40">
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
                {t("homepage.cta.title", "Contact us to get started")}
              </h2>
              <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
                {t(
                  "homepage.cta.subtitle",
                  "We will create and publish a professional landing page for your business, and help you set everything up."
                )}
              </p>

              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={SOCIAL.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-blue-600 text-white font-semibold shadow-sm hover:bg-blue-700 transition focus:outline-none focus:ring-4 focus:ring-blue-200/70"
                >
                  <MessageCircle className="h-5 w-5" />
                  {t("homepage.cta.whatsapp", "WhatsApp")}
                </a>

                <a
                  href="tel:0524657778"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 transition font-semibold text-slate-900"
                >
                  <PhoneCall className="h-5 w-5" />
                  0524657778
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-800 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 shrink-0 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-center shadow-sm">
                <img src={logo} alt="SmartBusiness" className="h-5 w-auto" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-slate-100 truncate">{t("homepage.brand", "SmartBusiness")}</div>
                <div className="text-sm text-slate-300">
                  {t("homepage.footer.developedBy", "Developed by")}{" "}
                  <span className="text-slate-100 font-semibold">Vynix</span>
                </div>
              </div>
            </div>

            <div className="md:justify-self-center">
              <div className="text-sm font-semibold mb-3 text-slate-100">{t("homepage.footer.social", "Social media")}</div>
              <div className="flex flex-wrap gap-3">
                <a
                  href={SOCIAL.facebook}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-800 bg-slate-900/40 hover:bg-slate-900 transition text-sm text-slate-200"
                >
                  <Facebook className="h-4 w-4" />
                  Facebook
                </a>
                <a
                  href={SOCIAL.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-800 bg-slate-900/40 hover:bg-slate-900 transition text-sm text-slate-200"
                >
                  <Instagram className="h-4 w-4" />
                  Instagram
                </a>
                <a
                  href={SOCIAL.tiktok}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-800 bg-slate-900/40 hover:bg-slate-900 transition text-sm text-slate-200"
                >
                  <Video className="h-4 w-4" />
                  TikTok
                </a>
                <a
                  href={SOCIAL.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-800 bg-slate-900/40 hover:bg-slate-900 transition text-sm text-slate-200"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </a>
              </div>
            </div>

            <div className={`${isRTL ? "md:justify-self-start" : "md:justify-self-end"}`}>
              <div className="text-sm font-semibold mb-3 text-slate-100">{t("homepage.footer.contact", "Contact us")}</div>
              <a
                href="tel:0524657778"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-800 bg-slate-900/40 hover:bg-slate-900 transition text-slate-100 font-semibold"
              >
                <PhoneCall className="h-4 w-4" />
                0524657778
              </a>
              <div className="mt-3 text-xs text-slate-400">
                © {new Date().getFullYear()} {t("homepage.brand", "SmartBusiness")}
              </div>
            </div>
          </div>

          <AccessibilityWidget direction={isRTL ? "RTL" : "LTR"} />
        </div>
      </footer>
    </div>
  );
}